import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { PII_MASK_CSS_CLASS } from '../../../../constants';
import { IconButton, IconButtonProps } from '../../../controls-and-inputs';

import styles from './SlideInPane.module.css';

export interface SlideInPaneProps {
  /* Whether the pane is displayed to the user */
  active: boolean;
  /* Callback to call when the user seeks to close the pane */
  onClose: () => void;
  /* Callback to call when the user seeks to open the pane using the trigger button */
  onOpen?: () => void;
  /* The width of the pane when expanded */
  expandedWidth?: string;
  dataHcName?: string;
  /* The button component that will expand and collapse the pane */
  triggerIconButtonProps?: Omit<IconButtonProps, 'className' | 'onClick'>;
  /* A JSX element that will be rendered in the top right corner of the pane when it's expanded,
   * to line up horizontally with the trigger button */
  topRightButton?: JSX.Element;
  children: React.ReactNode;
  /* Whether to render a spacer that will cause sibling content of the pane to shift as the pane displays.
   * When falsy, the pane will overlap page content and a background screen will cover the page */
  withSpacer?: boolean;
  theme?: {
    SlideInPane?: string;
    Pane?: string;
    Expanded?: string;
    Screen?: string;
    Spacer?: string;
    TriggerButton?: string;
    ScrollableContainer?: string;
    ActionsContainer?: string;
  };
  className?: string;
  fromSide?: 'left' | 'right';
  transparentScreen?: boolean;
  noCapture?: boolean;
}

const DEFAULT_EXPANDED_WIDTH = '313px';
const SLIDE_DURATION = 0.2;

/**
 * A pane that slides in from the left or right side of the screen. Its opened/closed state is managed externally
 * via `active` and is aided by onOpen and onClose callbacks that allow it to be opened/closed from click
 * actions on elements inside this component (i.e. the trigger button and screen overlay).
 */
export const SlideInPane = (props: SlideInPaneProps) => {
  const {
    active,
    onClose,
    noCapture = false,
    onOpen,
    expandedWidth = DEFAULT_EXPANDED_WIDTH,
    triggerIconButtonProps,
    topRightButton,
    dataHcName,
    withSpacer,
    children,
    theme,
    className,
    fromSide = 'left',
    transparentScreen,
  } = props;

  const hasActionsContainer = !!(triggerIconButtonProps || topRightButton);

  // Calculate initial position based on which side the pane slides from
  const initialX = fromSide === 'right' ? '100%' : '-100%';
  const activeX = 0;

  return (
    <AnimatePresence>
      <div
        className={classNames(
          styles.SlideInPane,
          className,
          theme?.SlideInPane,
          {
            [styles.Expanded]: active,
            [styles.WithSpacer]: withSpacer,
            [styles.WithActionsContainer]: hasActionsContainer,
            [styles.FromRight]: fromSide === 'right',
            [PII_MASK_CSS_CLASS]: noCapture,
            ...(theme?.Expanded && { [theme?.Expanded]: active }),
          },
        )}
      >
        {hasActionsContainer && (
          <div
            key="actions"
            className={classNames(
              styles.ActionsContainer,
              theme?.ActionsContainer,
            )}
            style={{ width: expandedWidth }}
          >
            {triggerIconButtonProps && (
              <IconButton
                {...triggerIconButtonProps}
                className={classNames(
                  styles.TriggerButton,
                  theme?.TriggerButton,
                )}
                onClick={() => (active ? onClose() : onOpen?.())}
              />
            )}
            {active && topRightButton}
          </div>
        )}
        <motion.div
          key="pane"
          initial={{ x: initialX }}
          animate={{
            x: active ? activeX : initialX,
            width: expandedWidth,
            transition: {
              duration: SLIDE_DURATION,
              type: 'easeInOut',
            },
          }}
          className={classNames(styles.Pane, theme?.Pane, {
            [styles.active]: active,
          })}
          data-hc-name={dataHcName}
        >
          {active && (
            <div
              className={classNames(
                styles.ScrollableContainer,
                theme?.ScrollableContainer,
              )}
            >
              {children}
            </div>
          )}
        </motion.div>
        {/* Only render the screen when the pane is overlapping page content (i.e. no spacer) */}
        {active && !withSpacer && (
          <div
            className={classNames(styles.Screen, theme?.Screen, {
              [styles.transparent]: transparentScreen,
            })}
            onClick={onClose}
          />
        )}
      </div>
      {withSpacer && active && (
        <motion.div
          key="spacer"
          initial={{ x: initialX }}
          animate={{
            x: active ? activeX : initialX,
            width: expandedWidth,
            transition: {
              type: 'easeInOut',
              duration: SLIDE_DURATION,
            },
          }}
          className={classNames(styles.Spacer, theme?.Spacer)}
        ></motion.div>
      )}
    </AnimatePresence>
  );
};
